import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  IconButton,
  Fab,
  Zoom,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Alert,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import NavigationIcon from "@mui/icons-material/Navigation";
import { useCookies } from "react-cookie";
import { useNavigate } from "react-router-dom"; // ADD THIS IMPORT
import { apiList, invokeApi } from "../../services/apiServices";
import { config } from "../../config/config";

export default function GalleryShowcase() { // REMOVE onImageSelect prop
  const [current, setCurrent] = useState(0);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cookies] = useCookies();
  const navigate = useNavigate(); // ADD THIS HOOK
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Fetch photos from API
  useEffect(() => {
    const fetchPhotos = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await invokeApi(
          config.getMyCollege + apiList.getPhotos,
          null,
          cookies
        );

        console.log("Photos API Response:", response);

        if (response?.status === 200) {
          let photosData = [];

          // Handle different response structures
          if (Array.isArray(response.data)) {
            photosData = response.data;
          } else if (response.data?.photos && Array.isArray(response.data.photos)) {
            photosData = response.data.photos;
          } else if (response.data?.data && Array.isArray(response.data.data)) {
            photosData = response.data.data;
          } else if (response.data?.items && Array.isArray(response.data.items)) {
            photosData = response.data.items;
          }

          // Filter only active photos and map to required format
          const formattedPhotos = photosData
            .filter(photo => photo.status === 'active')
            .map(photo => ({
              id: photo.id,
              image: photo.featuredImage,
              title: `Photo ${photo.id}`,
              status: photo.status,
              createdBy: photo.createdBy,
              createdDate: photo.createdDate,
              updatedBy: photo.updatedBy,
              updatedDate: photo.updatedDate
            }));

          setPhotos(formattedPhotos);
          console.log("Formatted photos:", formattedPhotos);
        } else {
          setError(`Failed to fetch photos: ${response?.status}`);
          console.error("Failed to fetch photos response:", response);
        }
      } catch (error) {
        console.error("Photos API error:", error);
        setError("Failed to load photos data");
      } finally {
        setLoading(false);
      }
    };

    fetchPhotos();
  }, [cookies]);

  const handleNext = () => {
    if (photos.length > 0) {
      setCurrent((prev) => (prev + 1) % photos.length);
    }
  };  

  const handlePrev = () => {
    if (photos.length > 0) {
      setCurrent((prev) => (prev - 1 + photos.length) % photos.length);
    }
  };

  // REPLACE handleImageClick with this:
  const handleImageClick = (photo) => {
    navigate(`/photo/${photo.id}`); // Use navigate instead of prop callback
  };

  const getItemIndices = () => {
    if (photos.length === 0) return [];
    if (isMobile) return [current];

    return [
      (current - 2 + photos.length) % photos.length,
      (current - 1 + photos.length) % photos.length,
      current,
      (current + 1) % photos.length,
      (current + 2) % photos.length,
    ];
  };

  const itemIndices = getItemIndices();

  if (loading) {
    return (
      <Box
        sx={{
          py: 6,
          textAlign: "center",
          px: 2,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "400px",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          py: 6,
          textAlign: "center",
          px: 2,
        }}
      >
        <Alert severity="error" sx={{ maxWidth: 600, mx: "auto" }}>
          {error}
        </Alert>
      </Box>
    );
  }

  if (photos.length === 0) {
    return (
      <Box
        sx={{
          py: 6,
          textAlign: "center",
          px: 2,
        }}
      >
        <Typography variant="h6" color="textSecondary">
          No photos available
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        py: 6,
        textAlign: "center",
        px: 2,
      }}
    >
      {/* ---- TITLE ---- */}
      <Typography
        variant="h3"
        fontWeight="bold"
        sx={{
          fontFamily: "Montserrat, sans-serif",
          mb: 1,
          color: "#2c3e50",
        }}
      >
        PHOTO GALLERY
      </Typography>

      <Typography
        variant="h4"
        sx={{
          color: "text.secondary",
          fontFamily: "Montserrat, sans-serif",
          mb: 2,
          fontWeight: 300,
        }}
      >
        Our Visual Collection
      </Typography>

      <Typography
        variant="h6"
        sx={{
          color: "text.secondary",
          fontFamily: "Montserrat, sans-serif",
          mb: 4,
          maxWidth: 600,
          mx: "auto",
        }}
      >
        Explore our curated collection of moments and memories
      </Typography>

      {/* ---- CAROUSEL WRAPPER ---- */}
      <Box
        sx={{
          position: "relative",
          maxWidth: isMobile ? 400 : 1200,
          mx: "auto",
          height: isMobile ? 300 : 400,
        }}
      >
        <Box
          sx={{
            position: "relative",
            height: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {itemIndices.map((imgIndex, i) => {
            const item = photos[imgIndex];
            const isCenter = imgIndex === current;

            let transform, zIndex, scale, opacity, width, height;

            if (isMobile) {
              transform = "translateX(0)";
              zIndex = 10;
              scale = 1;
              opacity = 1;
              width = 300;
              height = 250;
            } else {
              const positionIndex = i - 2;

              if (positionIndex === 0) {
                transform = "translateX(0)";
                zIndex = 10;
                scale = 1;
                opacity = 1;
                width = 400;
                height = 350;
              } else if (Math.abs(positionIndex) === 1) {
                transform = `translateX(${positionIndex * 270}px)`;
                zIndex = 9;
                scale = 0.9;
                opacity = 1;
                width = 350;
                height = 300;
              } else {
                transform = `translateX(${positionIndex * 230}px)`;
                zIndex = 8;
                scale = 0.8;
                opacity = 1;
                width = 300;
                height = 250;
              }
            }

            return (
              <Box
                key={item.id}
                sx={{
                  position: "absolute",
                  transform: `${transform} scale(${scale})`,
                  zIndex,
                  opacity,
                  transition: "all 0.5s ease",
                  cursor: "pointer",
                }}
                onClick={() => handleImageClick(item)} // PASS THE ITEM OBJECT
              >
                <Box
                  sx={{
                    width,
                    height,
                    borderRadius: 2,
                    overflow: "hidden",
                    backgroundColor: "#fff",
                    boxShadow: isCenter
                      ? "0 15px 30px rgba(0,0,0,0.2)"
                      : "0 8px 16px rgba(0,0,0,0.1)",
                    position: "relative",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: isCenter ? "scale(1.02)" : "scale(1.05)",
                    },
                  }}
                >
                  <Box
                    component="img"
                    src={item.image}
                    alt={item.title}
                    sx={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                    onError={(e) => {
                      e.target.src = "/default-image.jpg";
                    }}
                  />

                  {isCenter && (
                    <Box
                      sx={{
                        position: "absolute",
                        bottom: 0,
                        left: 0,
                        right: 0,
                        background:
                          "linear-gradient(to top, rgba(0,0,0,0.7) 0%, transparent 100%)",
                        color: "white",
                        p: 2,
                      }}
                    >
                      {/* Optional: Add overlay content here */}
                    </Box>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* ---- ARROWS BELOW CAROUSEL ---- */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: "20px",
          mt: 3,
        }}
      >
        <IconButton
          sx={{
            border: "2px solid #000",
            borderRadius: "50%",
            padding: "10px",
          }}
          onClick={handlePrev}
          disabled={photos.length === 0}
        >
          <ArrowBackIcon />
        </IconButton>

        <IconButton
          sx={{
            border: "2px solid #000",
            borderRadius: "50%",
            padding: "10px",
          }}
          onClick={handleNext}
          disabled={photos.length === 0}
        >
          <ArrowForwardIcon />
        </IconButton>
      </Box>

      {/* ---- FLOATING FAB ---- */}
      <Zoom in={true}>
        <Fab
          variant="extended"
          color="primary"
          sx={{
            position: "fixed",
            bottom: 16,
            right: 16,
          }}
        >
          <NavigationIcon sx={{ mr: 1 }} />
          Explore More
        </Fab>
      </Zoom>
    </Box>
  );
}